import React, { memo, useMemo } from 'react'

import { useNavigate } from 'react-router-dom'

import { LoginSessionType } from '@sportworld/js-sdk/src/constants'

import { getPagePopupById } from '@/actions'
import { Button } from '@/components/Button/Button'
import {
  LabelIcon,
  LockIcon,
  NonWatchableLabel,
  PremiumLabel,
  PurchasedLabel,
  TimeLabel,
} from '@/components/EventHeading/styles'
import { focusIds } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { paths } from '@/constants/paths'
import { useOpenStageTileContent } from '@/containers/HomeTabPage/hooks/useOpenStageTileContent'
import { useCustomFocus, useRedeemablePPVEvent } from '@/hooks'
import { usePageOptionsUpdate } from '@/hooks/usePageOptionsUpdate'
import { usePageRefresher } from '@/hooks/usePageRefresher'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import { StageSlide } from '@/types/servicesTypes/configTypes'

import {
  ChannelLogo,
  ChannelLogoWrapper,
  PromotionLogo,
  SecondaryTileButton,
  StageBadges,
  StageTileContent,
  StageTileEl,
  StageTileFooter,
  StageTileFooterRow,
  StageTileHeader,
  TokensContainer,
} from '../styles'
import { getStageSlideMeta } from '../utils/utils'

type StageSlideProps = {
  stageSlide: StageSlide
  index: number
  currentTabIndex: number
  isRegisteredUser: boolean
  withAnimation: boolean
  visibilityStatus: string
  focusIdPrefix?: string
  isPopup?: boolean
  isRequiresRegistrationOnly?: boolean
}

const HomePageStageTile = ({
  stageSlide,
  index,
  isRegisteredUser,
  currentTabIndex,
  withAnimation,
  visibilityStatus,
  focusIdPrefix = '',
  isPopup,
  isRequiresRegistrationOnly,
}: StageSlideProps) => {
  const setAuthPopup = useStore((state) => state.common.setAuthPopup)

  const isChannelSlide = stageSlide?.type === 'Channel'
  const channelId = isChannelSlide && stageSlide?.channel?.id
  const channelEvent = useStore(
    (state) => channelId && state.common.channelEvents[channelId],
  )
  const currentEvent = isChannelSlide ? channelEvent : stageSlide?.event
  const isRedeemablePPVEvent = useRedeemablePPVEvent(currentEvent || null)
  const { pagePopup } = useStore((state) => state.popup)

  const navigate = useNavigate()
  const { t } = useCommonTranslation()

  const focusId = `${focusIdPrefix}_HomePageStageTile_${index}`
  const { defaultFocusChild, onMove } = useCustomFocus({
    focusId,
  })
  const { pageRefresh } = usePageRefresher()

  const {
    stageTileTitle,
    stageTileDescription,
    stageTileButtonText,
    stageTileBadges,
    logoUrl,
    isBlockedStage,
    isButtonHidden,
    secondaryButtonVariant,
    tokens,
  } = useMemo(() => {
    return getStageSlideMeta(
      stageSlide,
      isRedeemablePPVEvent,
      false,
      isRegisteredUser,
    )
  }, [stageSlide, channelEvent, isRedeemablePPVEvent])

  const onFooterButtonClick = useOpenStageTileContent()

  const { resetPageOptions } = usePageOptionsUpdate(isPopup)

  const goToLogin = () => {
    resetPageOptions()

    const search = `?tabIndex=${currentTabIndex}&slideIndex=${index}`

    return setAuthPopup({
      sessionType: LoginSessionType.LOGIN,
      prevFocusId: focusId,
      redirectPath: pagePopup ? `${paths.PAGE}/${pagePopup.id}${search}` : '',
      onSuccessHandler: pagePopup ? undefined : () => pageRefresh(search),
    })
  }

  const goToSubscriptions = () => {
    return navigate(
      `${paths.SETTINGS}/${paths.SETTINGS_SUBSCRIPTIONS}?withFocus`,
    )
  }

  return (
    <StageTileEl
      orientation="horizontal"
      defaultFocusChild={defaultFocusChild}
      focusId={focusId}
      onMove={onMove}
      onRight={(e) => {
        if (secondaryButtonVariant && defaultFocusChild === 0) {
          e.stopPropagation()
        }
      }}
      $withAnimation={withAnimation}
      $visibilityStatus={visibilityStatus}
    >
      {tokens && (
        <TokensContainer>
          {t('stagePromotionTokens', {
            walletName: tokens.walletName,
            count: tokens.amount,
          })}
          <img src={icons.register.swPromotionCredits} alt="" />
        </TokensContainer>
      )}
      {stageTileTitle && <StageTileHeader>{stageTileTitle}</StageTileHeader>}
      {stageTileDescription && (
        <StageTileContent>{stageTileDescription}</StageTileContent>
      )}

      <StageTileFooter>
        {stageTileBadges && (
          <StageTileFooterRow>
            <StageBadges>
              {(isChannelSlide || !!stageTileBadges.timeLabel) && (
                <TimeLabel
                  $isLive={isChannelSlide}
                  $isLiveNow={stageTileBadges.isLiveNow && !isChannelSlide}
                  $isToday={stageTileBadges.isTodayEvent}
                >
                  {isChannelSlide ? t('liveTV') : stageTileBadges.timeLabel}
                </TimeLabel>
              )}
              {stageTileBadges.isBlockedPPVEvent && (
                <PremiumLabel>
                  <LabelIcon src={icons.navigation.blockedIcon} />
                  {stageTileBadges.isBlockedPromotionEvent
                    ? t('ppvPopup.promotion')
                    : t('ppvPopup.payPerView')}
                </PremiumLabel>
              )}
              {stageTileBadges.isBlockedPremiumChannel && (
                <PremiumLabel>
                  <LabelIcon src={icons.navigation.blockedIcon} />
                  {t('premiumPass')}
                </PremiumLabel>
              )}
              {(stageTileBadges.isSubscribedPremiumChannel ||
                stageTileBadges.isPurchasedPPVEvent) && (
                <PurchasedLabel>
                  <LabelIcon src={icons.navigation.subscribedIcon} />
                  {stageTileBadges.isSubscribedPremiumChannel
                    ? t('subscribed')
                    : t('bought')}
                </PurchasedLabel>
              )}
              {!stageTileBadges.isWatchable && (
                <NonWatchableLabel>
                  <LabelIcon src={icons.navigation.infoIcon} />
                  {t('programInfoOnly')}
                </NonWatchableLabel>
              )}
              {stageTileBadges.channelLogoImg && (
                <ChannelLogoWrapper>
                  <ChannelLogo src={stageTileBadges.channelLogoImg} />
                </ChannelLogoWrapper>
              )}
              {stageTileBadges.isLockEvent && (
                <LockIcon src={icons.navigation.lockIcon} alt="" />
              )}
            </StageBadges>
          </StageTileFooterRow>
        )}
        {logoUrl && (
          <div>
            <PromotionLogo src={logoUrl} alt="" />
          </div>
        )}
        <StageTileFooterRow>
          {!isButtonHidden ? (
            !isBlockedStage ? (
              <>
                <Button
                  focusId={`${focusIdPrefix}_HomePageStageTileButton_${index}`}
                  text={stageTileButtonText}
                  onClick={() => {
                    resetPageOptions()
                    onFooterButtonClick(stageSlide)
                  }}
                />
                {secondaryButtonVariant === 'partnerPage' && (
                  <SecondaryTileButton
                    focusId={`${focusIdPrefix}_HomePageStageTileButton_${index}_secondary`}
                    text={t('learnMore')}
                    onClick={() => {
                      resetPageOptions()
                      stageSlide?.promotion?.additionalPartnerPageId &&
                        getPagePopupById(
                          stageSlide.promotion.additionalPartnerPageId,
                        )
                    }}
                  />
                )}
              </>
            ) : (
              <>
                {isRegisteredUser ? (
                  <Button
                    iconSrc={icons.navigation.idCard}
                    text={t('unlockPremium')}
                    focusId={`${focusIdPrefix}_HomePageStageTileButton_${index}_${focusIds.UNLOCK_PREMIUM}`}
                    onClick={goToSubscriptions}
                  />
                ) : (
                  <Button
                    iconSrc={icons.register.loginIcon}
                    text={t(
                      isRequiresRegistrationOnly ? 'loginAndWatch' : 'login',
                    )}
                    focusId={`${focusIdPrefix}_HomePageStageTileButton_${index}_${focusIds.LOGIN}`}
                    onClick={goToLogin}
                  />
                )}
              </>
            )
          ) : null}
        </StageTileFooterRow>
      </StageTileFooter>
    </StageTileEl>
  )
}

export const StageTile = memo(HomePageStageTile)
